import { GroupAdd as GroupAddIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormHelperText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Tooltip,
} from "@mui/material";

const tFilteringByRoles = "Filtering by {count} roles";
const tNoRoleFilter = "No role filter applied";

const tAddAllFromRoles = "Add all users with selected roles";

interface RoleMultiSelectProps {
  availableRoles: string[];
  selectedRoles: string[];
  onChange: (roles: string[]) => void;
  availableUsers?: { id: string; roles: string[] }[];
  onAddUsers?: (userIds: string[]) => void;
}

export const RoleMultiSelect = ({
  availableRoles,
  selectedRoles,
  onChange,
  availableUsers = [],
  onAddUsers,
}: RoleMultiSelectProps): JSX.Element => {
  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const newSelectedRoles = event.target.value as string[];
    onChange(newSelectedRoles);
  };

  const handleAddAllUsers = () => {
    if (!onAddUsers || selectedRoles.length === 0) {
      return;
    }

    // Get all user IDs that have any of the selected roles
    const userIds = availableUsers
      .filter((user) => user.roles.some((role) => selectedRoles.includes(role)))
      .map((user) => user.id);

    onAddUsers(userIds);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Select
            multiple
            fullWidth
            value={selectedRoles}
            onChange={handleSelectChange}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    onDelete={() => {
                      const newSelectedRoles = selectedRoles.filter((r) => r !== role);
                      onChange(newSelectedRoles);
                    }}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    size="small"
                    sx={{
                      backgroundColor: "action.selected",
                      fontSize: "0.75rem",
                      height: "24px",
                    }}
                  />
                ))}
              </Box>
            )}
            sx={{
              "& .MuiSelect-select": {
                maxHeight: "80px",
                overflow: "auto",
              },
            }}
          >
            {availableRoles.map((role) => (
              <MenuItem
                key={role}
                value={role}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                {role}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {onAddUsers && (
          <Tooltip title={tAddAllFromRoles}>
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddAllUsers}
                disabled={selectedRoles.length === 0}
                sx={{
                  minWidth: 0,
                  p: 1,
                }}
              >
                <GroupAddIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
      <FormHelperText>
        {selectedRoles.length ? tFilteringByRoles.replace("{count}", String(selectedRoles.length)) : tNoRoleFilter}
      </FormHelperText>
    </Box>
  );
};
